import React from "react";
import { Box, MenuItem, FormControl } from "@mui/material";
import { useSelector } from "react-redux";
import { CompanyFormData } from "../../types/company-types";
import { RootState } from "../../../../store/Index";
import {
  FieldLabel,
  StyledSelect,
  StyledTextField,
} from "../../types/company-form";
import { MuiTelInput, MuiTelInputInfo } from "mui-tel-input";
import { normalizeUrl } from "../../../../utils/normalizeUrl";

interface Step1IdentityProps {
  formData: CompanyFormData;
  setFormData: (updater: (prev: CompanyFormData) => CompanyFormData) => void;
}

// REPLACE the INDUSTRIES array:
const INDUSTRIES = [
  { value: "streaming_company", label: "Streaming Company" },
  { value: "funeral_home", label: "Funeral Home" },
  { value: "insurance", label: "Insurance" },
  { value: "hospital", label: "Hospital" },
  { value: "clinic", label: "Clinic" },
  { value: "pharmacy", label: "Pharmacy" },
  { value: "law_firm", label: "Law Firm" },
  { value: "accounting_firm", label: "Accounting Firm" },
  { value: "real_estate", label: "Real Estate" },
  { value: "logistics", label: "Logistics & Transport" },
  { value: "retail", label: "Retail" },
  { value: "hospitality", label: "Hospitality & Tourism" },
  { value: "construction", label: "Construction" },
  { value: "education", label: "Education" },
  { value: "ngo", label: "NGO / Non-Profit" },
  { value: "government", label: "Government" },
  { value: "other", label: "Other" },
];

export default function Step1Identity({
  formData,
  setFormData,
}: Step1IdentityProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const cities: string[] = reduxTheme.cities ?? [];

  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];

  const set =
    (field: keyof CompanyFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handlePhoneChange = (value: string, _info: MuiTelInputInfo) => {
    void _info;
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  return (
    <Box>
      <FieldLabel
        textcolor={color100}
        sx={{ fontSize: "16px", fontWeight: 700, mb: 0.5 }}
      >
        Company Information
      </FieldLabel>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Company Name */}
        <Box>
          <FieldLabel textcolor={color100}>
            Company Name <span className="required">*</span>
          </FieldLabel>
          <StyledTextField
            name="name"
            placeholder="e.g. Muzukuru Funeral Streaming Company"
            value={formData.name}
            onChange={set("name")}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        {/* Industry + Phone row */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 280px) minmax(0, 280px)",
            gap: 2,
          }}
        >
          <Box>
            <FieldLabel textcolor={color100}>Industry</FieldLabel>
            <FormControl fullWidth>
              <StyledSelect
                name="industry"
                value={formData.industry}
                displayEmpty
                selectbg={color800}
                textcolor={color700}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    industry: String(e.target.value),
                  }))
                }
                renderValue={(selected) =>
                  selected ? (
                    <span style={{ color: color700 }}>
                      {selected as string}
                    </span>
                  ) : (
                    <span style={{ color: color300 }}>Select industry...</span>
                  )
                }
              >
                <MenuItem value="">
                  <em>Select industry</em>
                </MenuItem>
                {INDUSTRIES.map((ind) => (
                  <MenuItem key={ind.value} value={ind.value}>
                    {ind.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>

          <Box>
            <FieldLabel textcolor={color100}>Phone</FieldLabel>

            <MuiTelInput
              value={formData.phone}
              onChange={handlePhoneChange}
              defaultCountry="ZW"
              variant="outlined"
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                disablePortal: true,
                PaperProps: {
                  sx: {
                    maxHeight: 250,
                    mt: 1,
                  },
                },
              }}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: color800,
                  borderRadius: "30px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  fontSize: "13px",
                  color: color700,
                },
                "& .MuiInputBase-input": {
                  padding: "10px 14px",
                  fontSize: "13px",
                  color: color700,
                },
              }}
            />
          </Box>
        </Box>
        {/* Email + Website row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 280px) minmax(0, 280px)",
            gap: 2,
          }}
        >
          <Box>
            <FieldLabel textcolor={color100}>Main Email</FieldLabel>
            <StyledTextField
              name="primaryEmail"
              placeholder="info@company.com"
              value={formData.primaryEmail}
              onChange={set("primaryEmail")}
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
          <Box>
            <FieldLabel textcolor={color100}>Website</FieldLabel>
            <StyledTextField
              name="website"
              placeholder="https://www.company.com"
              value={formData.website}
              onChange={set("website")}
              onBlur={() =>
                setFormData((prev) => ({
                  ...prev,
                  website: normalizeUrl(prev.website),
                }))
              }
              inputbg={color800}
              textcolor={color700}
              placeholdercolor={color300}
            />
          </Box>
        </Box>
        {/* Address */}
        <Box>
          <FieldLabel textcolor={color100}>Address</FieldLabel>
          <StyledTextField
            name="address"
            placeholder="e.g. 123 Main Street, Harare"
            value={formData.address}
            onChange={set("address")}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Box>
        <Box>
          <FieldLabel textcolor={color100}>City</FieldLabel>
          <FormControl fullWidth sx={{ maxWidth: 280 }}>
            <StyledSelect
              name="city"
              value={formData.city}
              displayEmpty
              selectbg={color800}
              textcolor={color700}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  city: String(e.target.value),
                }))
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select city...</span>
                )
              }
              MenuProps={{
                PaperProps: { sx: { maxHeight: 280 } },
              }}
            >
              <MenuItem value="">
                <em>Select city</em>
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
}
